/**
 * The token in a /confirm-email link is the credential (XN-4561). The route's
 * guard takes it out of the address bar before anything on the page runs: the
 * sign-in layout asks the server for its settings before the page itself
 * mounts, and that request would carry the full URL as its Referer. The token
 * waits here, in memory, for the page to spend; the Referer stays off while
 * the page is shown.
 */
let held = null;

const REFERRER = 'meta[name="referrer"][data-confirm-email]';

// The router is not strict, so /confirm-email/ is the same page.
const isConfirmPage = (path) => path.replace(/\/+$/, '') === '/confirm-email';

function noReferrer() {
  if (document.head.querySelector(REFERRER)) return;
  const meta = document.createElement('meta');
  meta.name = 'referrer';
  meta.content = 'no-referrer';
  meta.dataset.confirmEmail = '';
  document.head.appendChild(meta);
}

/**
 * The first thing the app does (index.js, before Faro and before it asks for
 * the hub's settings or the user, all ahead of the router): a link opened
 * cold has its token taken from the address bar itself, so no bootstrap
 * request carries it in its Referer. Two tokens are held as both, which the
 * page reads as a link that cannot work, as it does through the router.
 */
export function holdConfirmTokenFromAddressBar() {
  const url = new URL(window.location.href);
  if (!isConfirmPage(url.pathname) || !url.searchParams.has('token')) return;

  const tokens = url.searchParams.getAll('token');
  held = tokens.length === 1 ? tokens[0] : tokens;
  noReferrer();
  url.searchParams.delete('token');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

/** The same, for a link followed inside the app: the route's guard. */
export function holdConfirmToken(to) {
  if (!('token' in to.query)) return true;

  held = to.query.token;
  noReferrer();

  // The same page with the token gone; other params and the hash stay. A
  // replace, so no history entry (Back, a reload) keeps it.
  const { token, ...rest } = to.query;
  return { path: to.path, query: rest, hash: to.hash, replace: true };
}

/** The held token, once: a second read gets nothing. */
export function takeConfirmToken() {
  const token = held;
  held = null;
  return token;
}

/** Referers again, once the page is gone. */
export function releaseReferrer() {
  document.head.querySelector(REFERRER)?.remove();
}

/**
 * After every navigation (router.afterEach): anywhere but this page, nothing
 * stays held. The page mounts only once the layout's settings request is back,
 * so leaving before then would otherwise keep the token for a later visit and
 * the Referer off for the rest of this one.
 */
export function forgetConfirmToken(to) {
  if (isConfirmPage(to.path)) return;
  held = null;
  releaseReferrer();
}
